import { decodeUriComponentSafely } from '../string-utils';

export function extractDomainFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);

    return urlObj.host;
  } catch {
    const match = url.match(/^https?:\/\/([^/]+)/);

    return match ? match[1] : url;
  }
}

export function normalizeTreePath(path: string): string {
  const [pathWithoutQueryAndHash] = path.split(/[?#]/);
  const pathname = pathWithoutQueryAndHash || '/';

  if (pathname !== '/' && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }

  return pathname;
}

function extractHashPath(hash: string): string | undefined {
  const normalizedHash = hash.startsWith('#!')
    ? hash.slice(2)
    : hash.startsWith('#')
      ? hash.slice(1)
      : hash;

  if (!normalizedHash.startsWith('/')) {
    return undefined;
  }

  const normalizedPath = normalizeTreePath(normalizedHash);

  return normalizedPath === '/' ? undefined : normalizedPath;
}

export function extractHashRouteFromUrl(url: string): string | undefined {
  try {
    const hash = new URL(url).hash;

    if (!hash || hash === '#') {
      return undefined;
    }

    const hashRoute = hash.startsWith('#!')
      ? hash.slice(2)
      : hash.startsWith('#')
        ? hash.slice(1)
        : hash;

    if (!hashRoute.startsWith('/')) {
      return undefined;
    }

    return hashRoute === '/' ? undefined : hashRoute;
  } catch {
    const match = url.match(/#(!?)(\/[^\s#]*)$/);
    const hashRoute = match?.[2];

    if (!hashRoute || hashRoute === '/') {
      return undefined;
    }

    return hashRoute;
  }
}

export function isHashbangRouterPageUrl(url: string): boolean {
  try {
    return new URL(url).hash.startsWith('#!/');
  } catch {
    return /#!\//.test(url);
  }
}

export function normalizeHashRouterPageUrl(url: string): string {
  if (!isHashRouterPageUrl(url)) {
    return url;
  }

  try {
    const parsed = new URL(url);
    const basePath = normalizeTreePath(parsed.pathname || '/');
    const documentBase =
      basePath === '/' ? `${parsed.origin}/` : `${parsed.origin}${basePath}/`;
    const hashRoute = extractHashRouteFromUrl(url);

    if (!hashRoute) {
      return documentBase;
    }

    const hashPrefix = isHashbangRouterPageUrl(url) ? '#!' : '#';

    return `${documentBase}${hashPrefix}${hashRoute}`;
  } catch {
    return url;
  }
}

export function isHashRouterPageUrl(url: string): boolean {
  try {
    const hash = new URL(url).hash;

    return hash.startsWith('#/') || hash.startsWith('#!/');
  } catch {
    return /#(!)?\//.test(url);
  }
}

export type SameDocumentHashNavigation =
  | { action: 'skip' }
  | { action: 'set-hash'; hashFragment: string }
  | { action: 'goto' };

export function resolveSameDocumentHashNavigation({
  currentUrl,
  targetUrl,
}: {
  currentUrl: string;
  targetUrl: string;
}): SameDocumentHashNavigation {
  if (!isHashRouterPageUrl(targetUrl) || currentUrl === 'about:blank') {
    return { action: 'goto' };
  }

  if (!isHashRouterPageUrl(currentUrl)) {
    return { action: 'goto' };
  }

  try {
    const current = new URL(currentUrl);
    const target = new URL(targetUrl);

    if (current.origin !== target.origin) {
      return { action: 'goto' };
    }

    if (
      normalizeTreePath(current.pathname || '/') !==
      normalizeTreePath(target.pathname || '/')
    ) {
      return { action: 'goto' };
    }

    if (
      isHashbangRouterPageUrl(currentUrl) !== isHashbangRouterPageUrl(targetUrl)
    ) {
      return { action: 'goto' };
    }

    const targetHashRoute = extractHashRouteFromUrl(targetUrl);

    if (!targetHashRoute) {
      return { action: 'goto' };
    }

    if (extractHashRouteFromUrl(currentUrl) === targetHashRoute) {
      return { action: 'skip' };
    }

    const hash = new URL(targetUrl).hash;
    const hashFragment = hash.startsWith('#') ? hash.slice(1) : hash;

    if (!hashFragment) {
      return { action: 'goto' };
    }

    return { action: 'set-hash', hashFragment };
  } catch {
    return { action: 'goto' };
  }
}

function normalizeLeadingSlashPath(path: string): string {
  return normalizeTreePath(path.startsWith('/') ? path : `/${path}`);
}

export function splitHashRoutedPath({
  path,
  documentPath = '/',
}: {
  path: string;
  documentPath?: string;
}): { documentPath: string; hashRoutePath: string } {
  const normalizedPath = normalizeLeadingSlashPath(path);
  const normalizedDocumentPath = normalizeLeadingSlashPath(documentPath);

  if (normalizedDocumentPath === '/') {
    return { documentPath: '/', hashRoutePath: normalizedPath };
  }

  const documentSegments = splitPathIntoSegments(normalizedDocumentPath);
  const pathSegments = splitPathIntoSegments(normalizedPath);

  if (pathSegments.length < documentSegments.length) {
    return { documentPath: '/', hashRoutePath: normalizedPath };
  }

  const hashSegments = pathSegments.slice(documentSegments.length);

  return {
    documentPath: normalizedDocumentPath,
    hashRoutePath:
      hashSegments.length === 0
        ? '/'
        : normalizeLeadingSlashPath(`/${hashSegments.join('/')}`),
  };
}

export function buildPageUrlFromOriginAndPath({
  origin,
  path,
  hashRouting = false,
  hashbang = false,
  documentPath = '/',
}: {
  origin: string;
  path: string;
  hashRouting?: boolean;
  hashbang?: boolean;
  documentPath?: string;
}): string {
  const normalizedPath = normalizeLeadingSlashPath(path);

  if (hashRouting) {
    const { documentPath: documentBase, hashRoutePath } = splitHashRoutedPath({
      path: normalizedPath,
      documentPath,
    });
    const documentUrlBase =
      documentBase === '/' ? `${origin}/` : `${origin}${documentBase}/`;

    if (hashRoutePath === '/') {
      return documentUrlBase;
    }

    return `${documentUrlBase}${hashbang ? '#!' : '#'}${hashRoutePath}`;
  }

  if (normalizedPath === '/') {
    return `${origin}/`;
  }

  return `${origin}${normalizedPath}`;
}

export function toWebpageParametrizationUrl(pageUrl: string): string {
  try {
    if (!isHashRouterPageUrl(pageUrl)) {
      return pageUrl;
    }

    const parsed = new URL(pageUrl);
    const path = extractPathFromUrl(pageUrl);

    if (path === '/') {
      return `${parsed.origin}/`;
    }

    return `${parsed.origin}${path}`;
  } catch {
    return pageUrl;
  }
}

export function shouldRestoreNavigationAnchor({
  anchorUrl,
  currentUrl,
}: {
  anchorUrl: string;
  currentUrl: string;
}): boolean {
  if (currentUrl === anchorUrl) {
    return false;
  }

  try {
    const anchor = new URL(anchorUrl);
    const current = new URL(currentUrl);

    if (anchor.origin !== current.origin) {
      return true;
    }

    if (isHashRouterPageUrl(anchorUrl) || isHashRouterPageUrl(currentUrl)) {
      return (
        normalizeTreePath(current.pathname || '/') !==
          normalizeTreePath(anchor.pathname || '/') ||
        extractHashRouteFromUrl(currentUrl) !==
          extractHashRouteFromUrl(anchorUrl)
      );
    }

    return (
      normalizeTreePath(current.pathname || '/') !==
        normalizeTreePath(anchor.pathname || '/') ||
      current.search !== anchor.search
    );
  } catch {
    return currentUrl !== anchorUrl;
  }
}

export function extractPathnameWithHashRoute(url: string): string {
  try {
    const parsed = new URL(url);
    const pathname = decodeUriComponentSafely(parsed.pathname || '/');
    const hash = parsed.hash;

    if (hash.startsWith('#/') || hash.startsWith('#!/')) {
      const base = pathname.endsWith('/') ? pathname : `${pathname}/`;
      const [hashWithoutQuery] = hash.split('?');

      return `${base}${hashWithoutQuery}`;
    }

    if (hash.length > 1) {
      return `${pathname.length > 0 ? pathname : '/'}${hash}`;
    }

    return pathname.length > 0 ? pathname : '/';
  } catch {
    const [path] = url.split('?');

    if (!path) {
      return '/';
    }

    return path.startsWith('/') ? path : `/${path}`;
  }
}

function combinePathWithHashRoute({
  pathname,
  hash,
}: {
  pathname: string;
  hash: string;
}): string {
  const hashPath = extractHashPath(hash);

  if (!hashPath) {
    return pathname;
  }

  if (pathname === '/') {
    return hashPath;
  }

  return normalizeTreePath(`${pathname}${hashPath}`);
}

export function extractPathFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const pathname = normalizeTreePath(
      decodeUriComponentSafely(urlObj.pathname || '/'),
    );

    return combinePathWithHashRoute({ pathname, hash: urlObj.hash });
  } catch {
    const trimmed = url.trim();

    if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
      const pathOnly = trimmed.split(/[?#]/)[0] || '/';
      const normalizedPathOnly = normalizeTreePath(
        decodeUriComponentSafely(pathOnly),
      );
      const hashMatch = trimmed.match(/#(!?\/.*)$/);

      return combinePathWithHashRoute({
        pathname: normalizedPathOnly,
        hash: hashMatch?.[0] ?? '',
      });
    }

    const match = url.match(/^https?:\/\/[^/]+(.*)$/);
    const pathname = match ? match[1] || '/' : '/';
    const [pathOnly] = pathname.split('#');
    const normalizedPath = normalizeTreePath(pathOnly || '/');
    const hashMatch = url.match(/#(!?\/.*)$/);

    return combinePathWithHashRoute({
      pathname: normalizedPath,
      hash: hashMatch?.[0] ?? '',
    });
  }
}

export function splitPathIntoSegments(path: string): string[] {
  return path.split('/').filter((segment) => segment.length > 0);
}

export function pathnameHasEmptySegment(url: string): boolean {
  try {
    return resolvePathnameForEmptySegmentCheck(url).includes('//');
  } catch {
    return false;
  }
}

function resolvePathnameForEmptySegmentCheck(url: string): string {
  if (url.includes('://')) {
    return new URL(url).pathname;
  }

  if (url.startsWith('//')) {
    return new URL(url, 'https://example.com').pathname;
  }

  return url.split(/[?#]/)[0] ?? '';
}

function isUrlSchemePath(path: string): boolean {
  const withoutLeadingSlash = path.replace(/^\//, '');

  return /^https?:($|\/)/i.test(withoutLeadingSlash);
}

export function getEndpointAssetPath({
  path,
  url,
}: {
  path: string;
  url: string;
}): string {
  const normalizedPath = normalizeTreePath(path || extractPathFromUrl(url));

  if (path && isUrlSchemePath(normalizedPath)) {
    return extractPathFromUrl(url);
  }

  return normalizedPath;
}

export function getWebPageAssetPath({ url }: { url: string }): string {
  return extractPathFromUrl(url);
}

export function formatWebPageRoleDisplayName(role: string): string {
  if (role === 'anonymous') {
    return 'public';
  }

  return role;
}
